<template>
  <teleport to="body">
  <div
    class="fixed inset-0 z-(--z-modal) flex items-center justify-center bg-backdrop"
    role="dialog"
    aria-modal="true"
    @mousedown.self="emit('close')"
  >
    <div class="flex w-90 max-w-[calc(100vw-2rem)] flex-col gap-4 rounded-control bg-bg2 p-4 text-text">
      <div class="text-group font-semibold">{{ t('menu.create_new_workspace') }}</div>
      <SingleLineInput
        v-model="name"
        :title="t('menu.workspace_name')"
        :maxLength="WORKSPACE_NAME_MAX_LENGTH"
        initialyFocused
        @keyup-enter="create"
      />
      <div class="flex items-center gap-2 text-body text-text-muted">
        <ToggleSwitch class="flex-none!" :modelValue="isPersonal" small @update:modelValue="togglePersonal" />
        {{ t('menu.personal_workspace') }}
      </div>
      <div v-if="error" class="text-label text-red">{{ error }}</div>
      <div class="flex justify-end gap-2">
        <SimpleButton variant="primary" size="md" :text="t('common.Cancel')" @click="emit('close')" />
        <SimpleButton
          variant="accent"
          size="md"
          :text="t('menu.create_workspace')"
          :loading="creating"
          :disabled="!name.trim()"
          @click="create"
        />
      </div>
    </div>
  </div>
  </teleport>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import SingleLineInput from '@/components/elements/SingleLineInput.vue';
import SimpleButton from '@/components/elements/SimpleButton.vue';
import ToggleSwitch from '@/components/elements/ToggleSwitch.vue';
import { isValidName, WORKSPACE_NAME_MAX_LENGTH } from '@/utils';

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const { user } = store.state.user;

const name = ref('');
const isPersonal = ref(false);
// what the name field shows the other way of the personal switch: at first the personal name, as the site's
// starting page offers it (the email's part before @ as a slug, or the user's name without an email)
let otherName = user.email
  ? user.email.split('@')[0].toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  : user.username;

function togglePersonal(value) {
  isPersonal.value = value;
  [name.value, otherName] = [otherName, name.value];
}

function onKeydown(e) {
  if (e.key === 'Escape') {
    emit('close');
  }
}

onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

const error = ref('');
const creating = ref(false);

async function create() {
  const wsname = name.value.trim();
  if (!wsname || creating.value) {
    return;
  }
  if (!isValidName(wsname, WORKSPACE_NAME_MAX_LENGTH)) {
    error.value = t('profile.username_invalid');
    return;
  }
  error.value = '';
  creating.value = true;
  try {
    await store.dispatch('user/actionCreateWorkspace', { wsname, isPersonal: isPersonal.value });
  } catch (e) {
    console.error('Workspace creation failed', e);
    error.value = t('menu.create_workspace_failed');
    creating.value = false;
  }
}
</script>
